// MCHD Room Scheduler - Security Monitoring Hook
// Custom hook for security monitoring and management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { 
  unlockAccount, 
  unlockIPAddress, 
  getFailedLoginAttempts,
  getSecurityStats,
  FailedAttemptsResponse,
  SecurityStatsResponse
} from '../../api/endpoints/auth';

// Real API function for getting failed login attempts
const getFailedAttemptsData = async (): Promise<FailedAttemptsResponse> => {
  return await getFailedLoginAttempts();
};

// Real API function for getting security stats
const getSecurityStatsData = async (): Promise<SecurityStatsResponse> => {
  return await getSecurityStats();
};

/**
 * Custom hook for security monitoring and management
 * @returns Security monitoring state and functions
 */
export function useSecurityMonitoring() {
  const queryClient = useQueryClient();

  // Get failed login attempts query
  const { 
    data: failedAttemptsData, 
    isLoading: attemptsLoading, 
    error: attemptsError 
  } = useQuery({
    queryKey: ['failed-attempts'],
    queryFn: getFailedAttemptsData,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Get security stats query
  const { 
    data: securityStatsData, 
    isLoading: statsLoading, 
    error: statsError 
  } = useQuery({
    queryKey: ['security-stats'],
    queryFn: () => getSecurityStatsData(),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
  });

  // Unlock account mutation
  const unlockAccountMutation = useMutation({
    mutationFn: unlockAccount,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['failed-attempts'] });
        queryClient.invalidateQueries({ queryKey: ['security-stats'] });
        notifications.show({
          title: 'Success',
          message: 'Account unlocked successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to unlock account',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to unlock account',
        color: 'red',
      });
    },
  });

  // Unlock IP address mutation
  const unlockIPMutation = useMutation({
    mutationFn: unlockIPAddress,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['failed-attempts'] });
        queryClient.invalidateQueries({ queryKey: ['security-stats'] });
        notifications.show({
          title: 'Success',
          message: 'IP address unlocked successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to unlock IP address',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to unlock IP address',
        color: 'red',
      });
    },
  });

  // Calculate security metrics
  const failedAttempts = failedAttemptsData?.data?.attempts || [];
  const securityStats = securityStatsData?.data?.stats || {
    totalAttempts: 0,
    unresolvedAttempts: 0,
    uniqueEmails: 0,
    uniqueIPs: 0,
    recentAttempts: 0,
    topOffendingIPs: [],
    topOffendingEmails: [],
    totalUsers: 0,
    activeUsers: 0,
    lockedAccounts: 0,
    activeSessions: 0,
  };

  const unresolvedAttempts = failedAttempts.filter(attempt => !attempt.isResolved);
  const recentAttempts = failedAttempts.filter(attempt => {
    const attemptedAt = new Date(attempt.attemptedAt);
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return attemptedAt > oneHourAgo;
  });

  const uniqueIPs = [...new Set(failedAttempts.map(attempt => attempt.ipAddress))];
  const uniqueEmails = [...new Set(failedAttempts.map(attempt => attempt.email))];

  const calculateSecurityScore = () => {
    const totalAttempts = securityStats.totalAttempts || 0;
    const unresolvedAttempts = securityStats.unresolvedAttempts || 0;
    const lockedAccounts = securityStats.lockedAccounts || 0;
    const uniqueIPs = securityStats.uniqueIPs || 0;
    const uniqueEmails = securityStats.uniqueEmails || 0;
    const recentAttempts = securityStats.recentAttempts || 0;
    const activeSessions = securityStats.activeSessions || 0;
    const totalUsers = securityStats.totalUsers || 1; // Avoid division by zero
    
    // Base score starts at 100
    let score = 100;
    
    // 1. Failed Attempts Impact (Weight: 30%)
    // Recent attempts are more concerning than old ones
    const recentAttemptsWeight = Math.min(recentAttempts * 3, 25);
    const totalAttemptsWeight = Math.min(totalAttempts * 1.5, 20);
    score -= recentAttemptsWeight + totalAttemptsWeight;
    
    // 2. Unresolved Security Issues (Weight: 25%)
    // Unresolved attempts indicate ongoing security problems
    score -= Math.min(unresolvedAttempts * 8, 25);
    
    // 3. Account Lockouts (Weight: 20%)
    // Locked accounts indicate serious security incidents
    score -= Math.min(lockedAccounts * 12, 20);
    
    // 4. Attack Sophistication (Weight: 15%)
    // More unique sources indicate more sophisticated attacks
    const attackSophistication = (uniqueIPs + uniqueEmails) * 2;
    score -= Math.min(attackSophistication, 15);
    
    // 5. System Health Indicators (Weight: 10%)
    // Active sessions vs total users ratio
    const sessionRatio = activeSessions / totalUsers;
    if (sessionRatio > 0.8) {
      score -= 5; // Too many active sessions might indicate security issues
    } else if (sessionRatio < 0.1) {
      score -= 3; // Very few active sessions might indicate system issues
    }
    
    // 6. Time-based Decay (Bonus)
    // Recent activity is weighted more heavily
    if (recentAttempts === 0 && totalAttempts > 0) {
      score += 5; // No recent attempts is good
    }
    
    // 7. Attack Pattern Analysis
    // Single source attacks are less concerning than distributed attacks
    if (uniqueIPs === 1 && uniqueEmails === 1 && totalAttempts < 10) {
      score += 3; // Single source, low volume attack
    } else if (uniqueIPs > 5 || uniqueEmails > 5) {
      score -= 5; // Distributed attack pattern
    }
    
    // 8. Zero-tolerance for critical issues
    if (lockedAccounts > totalUsers * 0.1) {
      score -= 10; // More than 10% of accounts locked
    }
    
    // Ensure score is between 0 and 100
    return Math.max(0, Math.min(100, Math.round(score)));
  };

  const getSecurityLevel = (score: number) => {
    if (score >= 95) return { level: 'Excellent', color: 'green' };
    if (score >= 85) return { level: 'Very Good', color: 'teal' };
    if (score >= 70) return { level: 'Good', color: 'blue' };
    if (score >= 50) return { level: 'Fair', color: 'yellow' };
    if (score >= 30) return { level: 'Poor', color: 'orange' };
    return { level: 'Critical', color: 'red' };
  };

  const securityScore = calculateSecurityScore();
  const securityLevel = getSecurityLevel(securityScore);

  // Generate security insights and recommendations
  const getSecurityInsights = () => {
    const insights = [];
    const recommendations = [];
    
    const lockedAccounts = securityStats.lockedAccounts || 0;
    const uniqueIPs = securityStats.uniqueIPs || 0;
    const recentAttempts = securityStats.recentAttempts || 0;
    const activeSessions = securityStats.activeSessions || 0;
    const totalUsers = securityStats.totalUsers || 1;
    
    // High priority insights
    if (lockedAccounts > 0) {
      insights.push({
        type: 'critical',
        message: `${lockedAccounts} account${lockedAccounts > 1 ? 's' : ''} currently locked`,
        action: 'Review and unlock legitimate accounts'
      });
    }
    
    if (recentAttempts > 5) {
      insights.push({
        type: 'warning',
        message: `${recentAttempts} failed attempts in the last hour`,
        action: 'Monitor for potential brute force attacks'
      });
    }
    
    if (uniqueIPs > 3) {
      insights.push({
        type: 'info',
        message: `Attacks from ${uniqueIPs} different IP addresses`,
        action: 'Consider implementing IP-based rate limiting'
      });
    }
    
    // Recommendations based on score
    if (securityScore < 50) {
      recommendations.push('Implement stronger password policies');
      recommendations.push('Enable two-factor authentication');
      recommendations.push('Review and update security monitoring');
    } else if (securityScore < 70) {
      recommendations.push('Monitor failed login attempts more closely');
      recommendations.push('Consider implementing account lockout policies');
    } else if (securityScore >= 90) {
      recommendations.push('Excellent security posture maintained');
      recommendations.push('Continue current security practices');
    }
    
    // System health recommendations
    const sessionRatio = activeSessions / totalUsers;
    if (sessionRatio > 0.8) {
      recommendations.push('High number of active sessions - review for suspicious activity');
    } else if (sessionRatio < 0.1 && totalUsers > 1) {
      recommendations.push('Low user activity - check system health');
    }
    
    return { insights, recommendations };
  };

  const securityInsights = getSecurityInsights();

  return {
    // Data
    failedAttempts,
    unresolvedAttempts,
    recentAttempts,
    securityStats,
    uniqueIPs,
    uniqueEmails,
    securityScore,
    securityLevel,
    securityInsights,
    isLoading: attemptsLoading || statsLoading,
    error: attemptsError?.message || statsError?.message || null,
    
    // Actions
    unlockAccount: unlockAccountMutation.mutate,
    unlockIP: unlockIPMutation.mutate,
    
    // Loading states
    isUnlockingAccount: unlockAccountMutation.isPending,
    isUnlockingIP: unlockIPMutation.isPending,
    
    // Security metrics
    metrics: {
      totalAttempts: securityStats.totalAttempts || failedAttempts.length,
      unresolvedAttempts: securityStats.unresolvedAttempts || unresolvedAttempts.length,
      recentAttempts: securityStats.recentAttempts || recentAttempts.length,
      uniqueIPs: securityStats.uniqueIPs || uniqueIPs.length,
      uniqueEmails: securityStats.uniqueEmails || uniqueEmails.length,
      securityScore: securityScore,
      securityLevel: securityLevel.level,
      securityColor: securityLevel.color,
      topOffendingIPs: securityStats.topOffendingIPs || [],
      topOffendingEmails: securityStats.topOffendingEmails || [],
      totalUsers: securityStats.totalUsers || 0,
      activeUsers: securityStats.activeUsers || 0,
      lockedAccounts: securityStats.lockedAccounts || 0,
      activeSessions: securityStats.activeSessions || 0,
    },
    
    // Refetch functions
    refetchAttempts: () => queryClient.invalidateQueries({ queryKey: ['failed-attempts'] }),
    refetchStats: () => queryClient.invalidateQueries({ queryKey: ['security-stats'] }),
    refetchAll: () => {
      queryClient.invalidateQueries({ queryKey: ['failed-attempts'] });
      queryClient.invalidateQueries({ queryKey: ['security-stats'] });
    },
  };
}
